<div class="container py-4">

  <!-- Loading list -->
  @if (state === 'loading') {
  <div class="text-center py-5">
    <div class="spinner-border text-primary" role="status">
      <span class="visually-hidden">Loading…</span>
    </div>
    <p class="mt-3 text-muted">{{ 'learnDeutschStories.loading' | transloco }}</p>
  </div>
  }

  <!-- List error -->
  @if (state === 'error') {
  <div class="row justify-content-center">
    <div class="col-md-5 text-center py-5">
      <i class="bi bi-exclamation-triangle-fill fs-1 text-danger"></i>
      <h5 class="mt-3">{{ 'learnDeutschStories.errorTitle' | transloco }}</h5>
      <p class="text-muted">{{ 'learnDeutschStories.errorMessage' | transloco }}</p>
      <button class="btn btn-outline-primary" (click)="loadList()">
        <i class="bi bi-arrow-clockwise me-1"></i>{{ 'learnDeutschStories.retry' | transloco }}
      </button>
    </div>
  </div>
  }

  <!-- Story list -->
  @if (state === 'list') {
  <div class="row justify-content-center">
    <div class="col-lg-8">
      <div class="text-center mb-4">
        <div class="fs-1">🇩🇪</div>
        <h3 class="mt-2 fw-bold">{{ 'learnDeutschStories.title' | transloco }}</h3>
        <p class="text-muted">{{ 'learnDeutschStories.subtitle' | transloco }}</p>
      </div>

      @if (stories.length === 0) {
        <p class="text-center text-muted py-4">{{ 'learnDeutschStories.empty' | transloco }}</p>
      }

      <div class="row g-3">
        @for (s of stories; track s.uuid) {
          <div class="col-12 col-md-6">
            <div class="card h-100 border-0 shadow-sm" style="cursor: pointer;"
                 (click)="openStory(s.uuid)">
              <div class="card-body">
                <div class="d-flex justify-content-between align-items-start mb-2">
                  <span class="badge {{ levelBadgeClass(s.level) }}">{{ s.level }}</span>
                  <span class="badge bg-light text-dark border text-capitalize">{{ s.topic }}</span>
                </div>
                <h5 class="fw-bold mb-0">{{ s.title }}</h5>
              </div>
              <div class="card-footer bg-transparent border-0 text-end">
                <span class="text-primary small fw-semibold">
                  {{ 'learnDeutschStories.start' | transloco }} <i class="bi bi-arrow-right ms-1"></i>
                </span>
              </div>
            </div>
          </div>
        }
      </div>

      <p class="mt-4 mb-0 text-center small text-muted">
        {{ 'learnDeutschStories.wantOwnWords' | transloco }}
        <a routerLink="/register" class="fw-semibold">{{ 'learnDeutschStories.createFreeAccount' | transloco }}</a>
      </p>
    </div>
  </div>
  }

  <!-- Loading single story -->
  @if (state === 'storyLoading') {
  <div class="text-center py-5">
    <div class="spinner-border text-primary" role="status">
      <span class="visually-hidden">Loading…</span>
    </div>
  </div>
  }

  <!-- Story error -->
  @if (state === 'storyError') {
  <div class="row justify-content-center">
    <div class="col-md-5 text-center py-5">
      <i class="bi bi-exclamation-triangle-fill fs-1 text-danger"></i>
      <h5 class="mt-3">{{ 'learnDeutschStories.errorTitle' | transloco }}</h5>
      <button class="btn btn-outline-secondary mt-2" (click)="backToList()">
        <i class="bi bi-arrow-left me-1"></i>{{ 'learnDeutschStories.backToStories' | transloco }}
      </button>
    </div>
  </div>
  }

  <!-- Assessment -->
  @if (state === 'assess' && story) {
  <div class="row justify-content-center">
    <div class="col-lg-8">

      <!-- Header -->
      <div class="d-flex justify-content-between align-items-center mb-3">
        <button class="btn btn-sm btn-outline-secondary" (click)="backToList()">
          <i class="bi bi-arrow-left me-1"></i>{{ 'learnDeutschStories.backToStories' | transloco }}
        </button>
        <span class="badge {{ levelBadgeClass(story.level) }}">{{ story.level }}</span>
      </div>

      <h4 class="fw-bold mb-1">{{ story.title }}</h4>
      <p class="text-muted small text-capitalize mb-3">{{ story.topic }}</p>

      <!-- Result banner -->
      @if (submitted) {
        <div class="card border-0 shadow-sm mb-3"
             [class.bg-success]="scorePercent === 100"
             [class.bg-opacity-10]="true"
             [class.bg-warning]="scorePercent < 100">
          <div class="card-body text-center py-3">
            <div class="fs-4 fw-bold">
              {{ 'learnDeutschStories.score' | transloco: { correct: correctCount, total: totalGaps } }}
            </div>
            <div class="small text-muted">{{ scorePercent }}%</div>
          </div>
        </div>
      }

      <!-- Story body with gaps -->
      <div class="card border-0 shadow-sm mb-3">
        <div class="card-body" style="line-height: 2.4; font-size: 1.1rem;">
          @for (seg of segments; track $index) {
            @if (seg.kind === 'text') {
              <span>{{ seg.text }}</span>
            } @else {
              <span class="d-inline-block align-middle mx-1">
                <select class="form-select form-select-sm d-inline-block w-auto align-baseline"
                        [ngClass]="gapSelectClass(seg.gap)"
                        [disabled]="submitted"
                        [value]="answers[seg.gap.position] || ''"
                        [title]="(gapHintKey(seg.gap) | transloco)
                                 + (gapCaseKey(seg.gap) ? ' · ' + (gapCaseKey(seg.gap)! | transloco) : '')"
                        (change)="setAnswer(seg.gap.position, $any($event.target).value)">
                  <option value="" disabled>—</option>
                  @for (opt of seg.gap.options; track opt.text) {
                    <option [value]="opt.text">{{ opt.text }}</option>
                  }
                </select>
                @if (submitted && !isGapCorrect(seg.gap)) {
                  <span class="badge bg-success ms-1">{{ correctAnswer(seg.gap) }}</span>
                }
              </span>
            }
          }
        </div>
      </div>

      <!-- Actions -->
      <div class="d-flex justify-content-between align-items-center">
        <span class="text-muted small">
          {{ 'learnDeutschStories.answered' | transloco: { answered: answeredCount, total: totalGaps } }}
        </span>
        @if (!submitted) {
          <button class="btn btn-primary" [disabled]="!allAnswered" (click)="submit()">
            <i class="bi bi-check2-circle me-1"></i>{{ 'learnDeutschStories.check' | transloco }}
          </button>
        } @else {
          <div class="d-flex gap-2">
            <button class="btn btn-outline-primary" (click)="retry()">
              <i class="bi bi-arrow-clockwise me-1"></i>{{ 'learnDeutschStories.tryAgain' | transloco }}
            </button>
            <button class="btn btn-outline-secondary" (click)="backToList()">
              {{ 'learnDeutschStories.backToStories' | transloco }}
            </button>
          </div>
        }
      </div>

    </div>
  </div>
  }

</div>
